import type { CellViewWire } from '@badger-sentinel/protocol';

import {
  CYBER_LAYER_LABELS,
  MAP_LAYER_LABELS,
  SPACE_LAYER_LABELS,
  type CyberLayers,
  type MapLayers,
  type SpaceLayers,
} from './map-layers.js';
import { missionLabel } from './missions.js';
import { canPlaceOnCyber, canPlaceOnOrbit, canPlaceOnTheatre } from './scenario.js';
import { naturalSlotFor } from './builder.js';
import type { QueuedOrder } from './queue.js';
import { targetWords, type Selection } from './selection.js';
import type { ContextMenuState, ContextSubject } from '../state/store.js';

/**
 * The context menu's four subjects (design 1616–1633, prototype `ctxVals`).
 *
 * One menu, four subjects — **unit · zone · order · canvas**. Phase 1 left the
 * primitive out deliberately: right-click is in the hotkey map bound to nothing,
 * and this is the phase that gives it a target. Phases 3–5 are its other
 * callers, which is why the items are *data* — a menu built by a component is a
 * menu each caller rebuilds.
 *
 * ## Only what exists is offered
 *
 * The prototype's menu has fifteen items across the four subjects and most of
 * them act on things this phase does not have: a task builder, a map, an order
 * queue, an undo stack. **A menu item with no target is not rendered disabled,
 * it is not rendered at all** (M5-D10). An operator who opens a menu of greyed
 * rows learns the console is mostly broken; one who opens a short menu learns
 * what the console can do today, and the menu grows as the phases land.
 *
 * Phase 2 had targets for select, filter, pin, batch and clear. Phase 3 adds the
 * builder and the queue, so the `unit` menu learns to start an order and the
 * `order` menu grows from one item to seven. `FOCUS ON MAP`, `ZOOM TO REGION` and
 * the layer toggles are still absent: Phase 5 owns the map they act on.
 */

export interface ContextItem {
  readonly id: string;
  readonly label: string;
  /** The key that does the same thing, when there is one. Never invented. */
  readonly hint?: string;
  readonly danger?: boolean;
  /** A rule is drawn *under* this item — the prototype's `sep`. */
  readonly separator?: boolean;
}

export interface ContextMenuModel {
  readonly title: string;
  readonly subtitle: string;
  readonly titleColour: string;
  readonly items: readonly ContextItem[];
}

export interface ContextMenuDeps {
  readonly view: CellViewWire | null;
  readonly queue: readonly QueuedOrder[];
  readonly pins: readonly Selection[];
  readonly batch: readonly string[];
  readonly hasSelection: boolean;
  /**
   * The map on screen, when there is one and it is drawing.
   *
   * `null` on a tab that has no canvas, and on a theatre with no map pack — the
   * camera items and the layer switches act on a map, so without one they are
   * not offered. That is the same rule as everything else in this file: a menu
   * item with no target is not rendered disabled, it is not rendered.
   *
   * The two arms are not interchangeable. `FIT TO FRIENDLY FORCES` exists on
   * `<region-map>` and is an empty function on `<orbital-map>`; `FOCUS ON MAP`
   * needs a subject the board in front of the operator can *place*, and a
   * satellite is not on the theatre map any more than a launcher is on a ring.
   */
  readonly map: MapContext | null;
}

export type MapContext =
  | { readonly kind: 'theatre'; readonly layers: MapLayers }
  | { readonly kind: 'space'; readonly layers: SpaceLayers }
  | { readonly kind: 'cyber'; readonly layers: CyberLayers };

export function contextMenuFor(
  menu: ContextMenuState,
  deps: ContextMenuDeps,
): ContextMenuModel | null {
  switch (menu.subject) {
    case 'unit':
      return unitMenu(menu.id, deps);
    case 'zone':
      return zoneMenu(menu.id, deps);
    case 'band':
      return bandMenu(menu.id, deps);
    case 'network':
      return networkMenu(menu.id, deps);
    case 'order':
      return orderMenu(menu.id, deps);
    case 'canvas':
      return canvasMenu(deps);
  }
}

function unitMenu(id: string, deps: ContextMenuDeps): ContextMenuModel | null {
  const view = deps.view;
  if (view === null) return null;

  const asset = view.friendly.find((candidate) => candidate.id === id);
  const contact = view.contacts.find((candidate) => candidate.id === id);
  const network = view.cyber.networks.find((candidate) => candidate.id === id);
  // A machine revealed *to cyber* is in none of the three: RB §5.6.2's
  // parenthesis keeps it out of `contacts[]`, and without this the cyber board's
  // right-click would open no menu at all on half of what it draws.
  const cyber = view.cyber.contacts.find((candidate) => candidate.alias === id);
  if (asset === undefined && contact === undefined && network === undefined && cyber === undefined)
    return null;

  const zone =
    asset !== undefined
      ? assetZone(asset.location)
      : contact !== undefined
        ? contactZone(contact.location)
        : // A cyber-only machine's zone is its enclave, which is the same facet
          // value the roster files cyber teams under.
          (cyber?.network ?? null);

  const items: ContextItem[] = [];
  if (asset !== undefined || contact !== undefined || cyber !== undefined) {
    items.push({ id: 'select', label: 'SELECT' });
  }
  // The design's two builder starters. Which one appears is decided by the same
  // function the Shift-click gesture uses, so a right-click and a modifier-click can
  // never disagree about what a row naturally is.
  if (asset !== undefined && naturalSlotFor(view, asset.id) === 'asset') {
    items.push({ id: 'set-asset', label: 'SET AS ASSET', hint: 'SHIFT CLICK' });
    items.push({ id: 'draft-from', label: 'DRAFT ORDER FROM HERE', separator: true });
  } else if (contact !== undefined || cyber !== undefined) {
    items.push({ id: 'set-target', label: 'SET AS TARGET', hint: 'ALT CLICK' });
    items.push({ id: 'draft-against', label: 'DRAFT ORDER AGAINST', separator: true });
  } else {
    const last = items[items.length - 1];
    if (last !== undefined) items[items.length - 1] = { ...last, separator: true };
  }
  // Publishing a contact to the team (RB §2.4) — the SDA and ISR seats' own act,
  // and the only item here that changes the live world rather than a draft. Not
  // offered to a seat without the capability and not repeated on a contact that
  // already carries a publisher: `contactRows` states that, and the rail's
  // `PublishControl` is the same rule in the same three states.
  if (contact !== undefined && contact.publishedBy === null && view.capabilities.canPublish) {
    items.push({ id: 'publish', label: 'PUBLISH TO TEAM', separator: true });
  }
  // The design's `FOCUS ON MAP`: switch to the board that owns this subject,
  // centre it, open its stack and flash it. Offered only for something that
  // board can actually place — a satellite is not on the theatre map.
  if (deps.map !== null && placeableOn(deps.map, view, id)) {
    items.push({ id: 'focus-map', label: 'FOCUS ON MAP', hint: 'F' });
  }
  if (zone !== null) items.push({ id: 'filter-zone', label: 'FILTER ROSTER TO ' + zone });
  // A cyber-only machine is included now that `resolveSelection` finds it: PIN
  // and ADD TO BATCH both act through a `Selection`, and before Phase 7 both
  // would have produced a compare slot reading `NOT IN THIS PICTURE`.
  if (asset !== undefined || contact !== undefined || cyber !== undefined) {
    items.push({
      id: 'pin',
      label: deps.pins.some((pin) => pin.id === id) ? 'UNPIN FROM COMPARE' : 'PIN TO COMPARE',
      hint: 'P',
    });
    items.push({
      id: 'batch',
      label: deps.batch.includes(id) ? 'REMOVE FROM BATCH' : 'ADD TO BATCH',
      hint: 'CTRL CLICK',
    });
  }
  const standing = deps.queue.filter(
    (entry) => entry.order.asset === id || targetWords(entry.order.target) === id,
  );
  if (standing.length > 0) {
    const last = items[items.length - 1];
    if (last !== undefined) items[items.length - 1] = { ...last, separator: true };
    items.push({ id: 'edit-order', label: 'EDIT ITS ORDER' });
    items.push({ id: 'duplicate-order', label: 'DUPLICATE ITS ORDER' });
    items.push({
      id: 'cancel-order',
      label: standing.length > 1 ? 'CANCEL ITS ORDERS' : 'CANCEL ITS ORDER',
      danger: true,
    });
  }
  if (items.length === 0) return null;

  return {
    title: id,
    subtitle:
      asset?.typeName ??
      (contact !== undefined
        ? contact.presentedTypeName + ' · ' + contact.rung
        : cyber !== undefined
          ? // Nothing has *looked* at it, so there is no presented type to name.
            // The enclave and the rung on it is the whole of what cyber knows.
            cyber.network + ' · ' + cyber.access
          : (network?.name ?? '')),
    titleColour: contact === undefined && cyber === undefined ? 'var(--blue)' : 'var(--red)',
    items,
  };
}

function zoneMenu(id: string, deps: ContextMenuDeps): ContextMenuModel | null {
  const region = deps.view?.map.regions.find((candidate) => candidate.id === id);
  if (region === undefined) return null;
  return {
    title: region.id,
    subtitle: region.name,
    titleColour: 'var(--text-2)',
    items: [
      { id: 'select', label: 'SELECT REGION' },
      ...(deps.map === null
        ? []
        : [{ id: 'zoom-zone', label: 'ZOOM TO REGION', hint: 'DBL-CLICK' } as ContextItem]),
      { id: 'filter-zone', label: 'FILTER ROSTER TO ' + region.id, separator: true },
    ],
  };
}

/**
 * The enclave subject — the design's `region` branch, third table.
 *
 * The design resolves one `region` id against three lookups and shows one set of
 * items (markup 2475–2495); this file keeps them apart because the *lookup* is
 * what fails when they are merged, and it keeps the items the same for the same
 * reason the design does — an operator right-clicking a heading wants to select
 * it, go to it, and filter the roster by it, whatever kind of zone it is.
 *
 * Two differences from `zoneMenu`, and both are about what the wire can carry.
 * `SET AS TARGET` is absent: `zOrderTarget` has no enclave arm, and the cyber
 * missions target *machines* — a menu item that produced no legal order is the
 * thing this file exists to refuse. And `ZOOM TO REGION` is offered only on the
 * cyber board, because `focusRouter` is only on `<cyber-map>`.
 */
function networkMenu(id: string, deps: ContextMenuDeps): ContextMenuModel | null {
  const network = deps.view?.cyber.networks.find((candidate) => candidate.id === id);
  if (network === undefined) return null;
  return {
    title: network.id,
    subtitle: network.name,
    titleColour: 'var(--text-2)',
    items: [
      { id: 'select', label: 'SELECT ENCLAVE' },
      ...(deps.map?.kind === 'cyber'
        ? [{ id: 'zoom-network', label: 'ZOOM TO ENCLAVE', hint: 'DBL-CLICK' } as ContextItem]
        : []),
      { id: 'filter-zone', label: 'FILTER ROSTER TO ' + network.id, separator: true },
    ],
  };
}

/**
 * The order subject (prototype `ctxVals`, the `order` branch).
 *
 * Seven items, and every one of them acts on something this phase built: the
 * ladder, the builder, the queue's own order. `RAISE` and `LOWER` are absent at
 * the ends rather than drawn disabled — an operator who opens a menu of greyed
 * rows learns the console is mostly broken, and the first and last cards are
 * exactly where somebody looks.
 */
function orderMenu(key: string, deps: ContextMenuDeps): ContextMenuModel | null {
  const index = deps.queue.findIndex((entry) => entry.key === key);
  const entry = deps.queue[index];
  if (entry === undefined) return null;
  const target = targetWords(entry.order.target);

  const items: ContextItem[] = [
    { id: 'why', label: 'SUCCESS BREAKDOWN' },
    { id: 'edit', label: 'EDIT IN BUILDER' },
    { id: 'duplicate', label: 'DUPLICATE' },
    { id: 'reassign', label: 'REASSIGN TO ANOTHER ASSET', separator: true },
  ];
  if (index > 0) items.push({ id: 'raise', label: 'RAISE PRIORITY' });
  if (index < deps.queue.length - 1) items.push({ id: 'lower', label: 'LOWER PRIORITY' });
  const last = items[items.length - 1];
  if (last !== undefined) items[items.length - 1] = { ...last, separator: true };
  items.push({ id: 'cancel', label: 'CANCEL ORDER', danger: true });

  return {
    title: entry.order.asset + (target === '' ? '' : ' → ' + target),
    subtitle: missionLabel(entry.order.mission),
    titleColour: 'var(--primary)',
    items,
  };
}

/**
 * The canvas subject (design 2513–2535).
 *
 * Two camera actions, the fit pair — *where am I, and where are they*, the two
 * questions a commander asks of a map before anything else — and the five layer
 * switches, each carrying its own state in the hint column as the design does.
 * The switches are here as well as in the LAYERS panel because a right-click is
 * where an operator already is when the map is not showing what they want.
 */
function canvasMenu(deps: ContextMenuDeps): ContextMenuModel | null {
  const items: ContextItem[] = [];
  const map = deps.map;
  if (map !== null) {
    // `R` is the orbital board's own key, bound inside the vendored element. The
    // hint names it only where it exists, which is what the design does too.
    items.push({
      id: 'reset-view',
      label: 'RESET VIEW',
      ...(map.kind === 'space' ? { hint: 'R' } : {}),
    });
    // The fit pair is `fitSide`, which `<orbital-map>` defines as an empty
    // function. An item wired to that is a control that does nothing.
    if (map.kind === 'theatre') {
      items.push({ id: 'fit-friendly', label: 'FIT TO FRIENDLY FORCES' });
      items.push({ id: 'fit-threats', label: 'FIT TO THREATS' });
    }
    const beforeLayers = items[items.length - 1];
    if (beforeLayers !== undefined) items[items.length - 1] = { ...beforeLayers, separator: true };
    // Read against its own table, so a key and its state are looked up together
    // and the two boards' four/five switches cannot be crossed.
    const switches: readonly (readonly [string, string, boolean])[] =
      map.kind === 'space'
        ? SPACE_LAYER_LABELS.map(([key, label]) => [key, label, map.layers[key]] as const)
        : map.kind === 'cyber'
          ? CYBER_LAYER_LABELS.map(([key, label]) => [key, label, map.layers[key]] as const)
          : MAP_LAYER_LABELS.map(([key, label]) => [key, label, map.layers[key]] as const);
    for (const [key, label, on] of switches) {
      items.push({ id: 'layer:' + key, label, hint: on ? '■' : '□' });
    }
    const last = items[items.length - 1];
    if (last !== undefined) items[items.length - 1] = { ...last, separator: true };
  }
  if (deps.hasSelection)
    items.push({ id: 'clear-selection', label: 'CLEAR SELECTION', hint: 'ESC' });
  if (deps.batch.length > 0) items.push({ id: 'clear-batch', label: 'CLEAR BATCH' });
  if (items.length === 0) return null;
  return {
    title: 'CANVAS',
    subtitle:
      map === null
        ? ''
        : map.kind === 'space'
          ? 'ORBITAL VIEW'
          : map.kind === 'cyber'
            ? 'NETWORK VIEW'
            : 'THEATRE VIEW',
    titleColour: 'var(--text)',
    items,
  };
}

/** Which board can place this subject — the same predicate `F` is gated on. */
function placeableOn(map: MapContext, view: CellViewWire, id: string): boolean {
  if (map.kind === 'space') return canPlaceOnOrbit(view, id);
  if (map.kind === 'cyber') return canPlaceOnCyber(view, id);
  return canPlaceOnTheatre(view, id);
}

/**
 * The band subject (Phase 6) — a ring, right-clicked.
 *
 * `zoneMenu`'s shape, resolved against `map.bandSlots` rather than
 * `map.regions`, because a band is a zone the ground has no name for. ZOOM TO
 * BAND is `focusRegime`, and FILTER ROSTER TO GEO works unchanged: `zoneOf`
 * already files an orbital asset under its band, so the left rail's facet
 * vocabulary and this one are the same word.
 */
function bandMenu(id: string, deps: ContextMenuDeps): ContextMenuModel | null {
  const slots = (deps.view?.map.bandSlots as Readonly<Record<string, number>> | undefined)?.[id];
  if (slots === undefined) return null;
  return {
    title: id,
    subtitle: 'ORBITAL BAND',
    titleColour: 'var(--text-2)',
    items: [
      { id: 'select', label: 'SELECT BAND' },
      ...(deps.map?.kind === 'space'
        ? [{ id: 'zoom-band', label: 'ZOOM TO BAND' } as ContextItem]
        : []),
      { id: 'set-target', label: 'SET AS TARGET', hint: 'ALT CLICK' },
      { id: 'draft-against', label: 'DRAFT ORDER AGAINST', separator: true },
      { id: 'filter-zone', label: 'FILTER ROSTER TO ' + id, separator: true },
    ],
  };
}

function assetZone(location: CellViewWire['friendly'][number]['location']): string | null {
  switch (location.kind) {
    case 'GROUND':
      return location.region;
    case 'ORBIT':
      return location.band;
    case 'TRANSIT':
      return location.to;
    case 'OFFMAP':
      return null;
  }
}

function contactZone(location: CellViewWire['contacts'][number]['location']): string | null {
  switch (location.kind) {
    case 'REGION':
      return location.region;
    case 'BAND':
    case 'SLOT':
      return location.band;
    case 'UNKNOWN':
      return null;
  }
}

/** Keeps the panel on screen: it opens at the cursor but never off the edge. */
export function contextMenuPosition(
  menu: ContextMenuState,
  itemCount: number,
  viewport: { readonly width: number; readonly height: number },
): { readonly left: number; readonly top: number } {
  const width = CONTEXT_MENU_WIDTH;
  const height = 40 + itemCount * 26 + 10;
  return {
    left: Math.max(6, Math.min(menu.x, viewport.width - width - 8)),
    top: Math.max(6, Math.min(menu.y, viewport.height - height - 8)),
  };
}

export const CONTEXT_MENU_WIDTH = 236;

export type { ContextSubject };
